import React, { PropsWithChildren } from "react"
import { makeStyles } from "tss-react/mui"
import { Grid, Typography } from "@mui/material"
import { StandardDialog } from "../../../../../../shared/components/dialogs/StandardDialog"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { EmailsResend, SupportEmail } from "../emailTypes"
import { Button } from "../../../../../office/admin/components/Button"
import { tailwindPalette } from "../../../../../theme/tailwindPalette"
import { Form } from "formik"
import { Formik } from "../../../../../../shared/components/forms/SchemaFormik"
import * as Yup from "yup"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { FormikTextField } from "../../../../../../shared/components/forms/Formik"

type ConfirmModalProps = Readonly<
    PropsWithChildren<{
        open: boolean
        onClose: () => void
        handleClick(data: EmailsResend["receiver"]): void
        data: SupportEmail
    }>
>

const useStyles = makeStyles()(() => ({
    dialogPaper: {
        padding: "1rem 4rem",
        minHeight: "320px",
        justifyContent: "center",
    },
    container: {
        gap: "2rem",
    },
    textGray: {
        color: tailwindPalette.gray[500],
    },
}))

export function ResendDialog({ open, onClose, handleClick, data }: ConfirmModalProps) {
    const { l10n } = useAppLocalization()
    const { classes, cx } = useStyles()

    const validationSchema = Yup.object().shape({
        to: Yup.string().required().email().label(l10n.getString("required-field")),
        cc: Yup.string().required().email().label(l10n.getString("required-field")),
        bcc: Yup.string().required().email().label(l10n.getString("required-field")),
    })

    const initialValues = {
        to: data.receiver.to ?? "",
        cc: data.receiver.cc ?? "",
        bcc: data.receiver.bcc ?? "",
    }

    return (
        <StandardDialog
            open={open}
            onClose={onClose}
            maxWidthProp="sm"
            dialogTitle={
                <LocalizedStrict id="support-messages-email-details-resend-email-title">
                    <>Resend email</>
                </LocalizedStrict>
            }
            slotProps={{ paper: { className: cx(classes.dialogPaper) } }}
            hideCloseIcon
        >
            <Formik
                enableReinitialize
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleClick}
            >
                <Form>
                    <Grid container className={classes.container}>
                        <Grid container gap=".5rem">
                            <Grid container alignItems="center">
                                <Grid item xs={2}>
                                    <LocalizedStrict id="support-messages-email-details-to-label">
                                        <Typography className={classes.textGray}>To:</Typography>
                                    </LocalizedStrict>
                                </Grid>
                                <Grid item xs={10}>
                                    <FormikTextField fullWidth name="to" />
                                </Grid>
                            </Grid>
                            <Grid container alignItems="center">
                                <Grid item xs={2}>
                                    <LocalizedStrict id="support-messages-email-details-cc-label">
                                        <Typography className={classes.textGray}>CC:</Typography>
                                    </LocalizedStrict>
                                </Grid>
                                <Grid item xs={10}>
                                    <FormikTextField fullWidth name="cc" />
                                </Grid>
                            </Grid>

                            <Grid container alignItems="center">
                                <Grid item xs={2}>
                                    <LocalizedStrict id="support-messages-email-details-bcc-label">
                                        <Typography className={classes.textGray}>BCC:</Typography>
                                    </LocalizedStrict>
                                </Grid>
                                <Grid item xs={10}>
                                    <FormikTextField fullWidth name="bcc" />
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid container alignItems="center" justifyContent="center">
                            <LocalizedStrict id="support-messages-email-details-sent-button">
                                <Button type="submit">Sent</Button>
                            </LocalizedStrict>
                        </Grid>
                    </Grid>
                </Form>
            </Formik>
        </StandardDialog>
    )
}
